import { usePTeamStore, router } from '@vas2t-lowcode/web';
import { DEFAULT_LANG, getLocalRespCodeData, showRespMessage } from '@vas2t-lowcode/utils';
import { AxiosInstance, AxiosInterceptorManager, AxiosResponse } from 'axios';
import { getApiCustomConfig } from '../config/custom';
import { AxiosRequestConfigExtends } from '../entities/axios-request-config-extends';

interface ResponseConfigType<T = unknown> extends Omit<AxiosResponse<T>, 'config'> {
  config: AxiosRequestConfigExtends;
}

// 储存现在显示状态的消息，重复的不再次显示
const msgShowedStack = new Map();
/**
 * 管理API错误的消息提示，相同的消息不重复显示
 * @param code
 * @param dynDataList
 * @returns
 */
const msgShowManageAsync = function (code: string, dynDataList: string[] = []) {
  // 已处于显示状态下的消息弹窗不再显示
  if (!msgShowedStack.has(code)) {
    msgShowedStack.set(code, null);
    return showRespMessage({
      dynDataList,
      code,
      onClose() {
        msgShowedStack.delete(code);
        // 判断是否强制刷新页面
        reloadPageAfterTipClose(code);
      },
    })?.then((msgInst) => {
      msgShowedStack.set(code, msgInst);
    });
  }
};

/**
 * 提示关闭后是否刷新页面
 *
 * @param code
 * @param lang
 * @returns
 */
export const reloadPageAfterTipClose = (code: string, lang = DEFAULT_LANG) => {
  if (!code) {
    return;
  }

  if (code === undefined || code === null) {
    return '';
  }

  // 获取响应码翻译后的内容
  const codeData = getLocalRespCodeData({ lang });
  if (!codeData) {
    return; // 查找不到对应响应信息，直接返回code值
  }

  // 根据响应code获取提示信息
  const respCodeMsgObj = codeData[code];
  if (!respCodeMsgObj) {
    return;
  }

  // 获取是否强制刷新页面
  const { forceReloadPage } = respCodeMsgObj;
  if (!forceReloadPage) {
    return;
  }

  if (['err_team_member_disabled', 'err_invalid_team_member'].indexOf(code) !== -1) {
    // 非法团队成员则返回首页
    router.push({ path: '/home', force: true }).finally(() => {
      location.reload();
    });
    return;
  }

  // 页面重新刷新
  location.reload();
};

/**
 * 获取全局的 dynList
 * @param code
 * @returns
 */
const getGlobalDynList = (code: string) => {
  let globalDynDataList = undefined as undefined | string[];
  // 全局错误拦截器
  if (['err_team_member_disabled', 'err_invalid_team_member'].indexOf(code) !== -1) {
    const teamStore = usePTeamStore();
    const workspaceName = teamStore.statics?.curr_team_info?.name || '';
    workspaceName && (globalDynDataList = [workspaceName]);
  }

  return globalDynDataList;
};

export function interceptResponse<T extends AxiosInterceptorManager<ResponseConfigType<{ result: string }>>>(
  service: AxiosInstance
) {
  // 响应拦截器
  (service.interceptors.response as T).use(
    (res) => {
      // // 更新网络状态
      // setNetworkError(false);
      const rData = res.data || {};
      const status = res.status ?? 404;
      const resultCode = rData.result;
      const { customConfig = {} } = res.config;

      if (status >= 200 && status < 300 && resultCode === 'ok') {
        // 正常请求
        return Promise.resolve(rData);
      }

      (async () => {
        // 未登录错误处理
        if (resultCode === 'no_login') {
          // 退出登录，清除相关数据
          const cfg = getApiCustomConfig();
          cfg.cb4Logout?.();
          if (customConfig.toLoginPage) {
            // url跳转登录页
            await cfg.toLoginPageAsync?.();
          }
        }

        // 处理错误提示信息，默认开启
        if (customConfig.showRespErrMsg) {
          // 获取全局的动态提示信息
          const globalDynDataList = getGlobalDynList(resultCode);
          // 显示接口错误提示
          await msgShowManageAsync(resultCode, customConfig.dynDataList || globalDynDataList);
        }
      })();

      // 错误向下传递
      return Promise.reject(rData);
    },
    (err) => {
      // #region 超时统计
      err.config.timeData = err.config.timeData || {};
      err.config.timeData.endTime = new Date();
      // #endregion

      // 非200报错
      let msg = err?.message || '';
      if (typeof err === 'string') {
        msg = err;
      }
      console.warn('响应拦截器错误', msg);

      // 检测是否是网路故障
      if (err.message === 'Network Error') {
        // 提示
        err.result = 'client_network_error';
      } else {
        // 其他错误
        err.result = 'error'; // err.message;
      }

      const { customConfig = {} } = err.config || {};
      if (customConfig.showRespErrMsg) {
        // 显示非常规错误信息
        msgShowManageAsync(err.result);
      }

      return Promise.reject(err);
    }
  );
}
